import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { userService } from '../services/userService';
import { authService } from '../services/authService';
import { useToast } from '../components/Toast';
import { getMediaUrl } from '../utils/media';
import { useTranslation } from 'react-i18next';
import { CommunitySettings } from '../features/settings/components/CommunitySettings';
import './SettingsPage.css';

type SettingsSection = 'community' | 'profile' | 'payment' | 'account' | 'notifications' | 'chat' | 'payment-methods' | 'payment-history' | 'theme';

const SettingsPage: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<SettingsSection>((section as SettingsSection) || 'community');

  // Profile settings
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    location: '',
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    socialLinks: false,
    membershipVisibility: false,
    advanced: false,
  });
  const [showChangeNameModal, setShowChangeNameModal] = useState(false);
  const [nameChangeData, setNameChangeData] = useState({
    first_name: '',
    last_name: '',
  });
  const [timeUntilNameChange, setTimeUntilNameChange] = useState<number | null>(null);

  // Account settings (for Account section)
  const [accountData, setAccountData] = useState({
    email: '',
    phone: '',
  });

  // Password change form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Modal for password change confirmation
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);

  // Privacy settings
  const [privacyData, setPrivacyData] = useState({
    profile_visibility: 'public',
    show_email: true,
    show_phone: false,
  });

  // Notification settings
  const [notificationData, setNotificationData] = useState({
    email_notifications: true,
    push_notifications: true,
    comment_notifications: true,
    post_notifications: true,
  });

  // Update active section when URL changes
  useEffect(() => {
    if (section) {
      setActiveSection((section as SettingsSection) || 'community');
    } else {
      navigate('/settings/community', { replace: true });
    }
  }, [section, navigate]);

  useEffect(() => {
    if (user) {
      const nameParts = (user.full_name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setProfileData({
        first_name: firstName,
        last_name: lastName,
        bio: user.bio || '',
        location: user.location || '',
      });

      setNameChangeData({
        first_name: firstName,
        last_name: lastName,
      });

      setAccountData({
        email: user.email || '',
        phone: user.phone || '',
      });

      if (user.avatar) {
        setAvatarPreview(getMediaUrl(user.avatar));
      }

      // Calculate time until name change
      if (user.name_changed_at) {
        const lastChange = new Date(user.name_changed_at);
        const now = new Date();
        const timeDiff = now.getTime() - lastChange.getTime();
        const hoursSinceChange = timeDiff / (1000 * 60 * 60);

        if (hoursSinceChange < 24) {
          const hoursRemaining = 24 - hoursSinceChange;
          setTimeUntilNameChange(Math.ceil(hoursRemaining * 60)); // in minutes
        } else {
          setTimeUntilNameChange(null);
        }
      } else {
        setTimeUntilNameChange(null);
      }
    }
  }, [user]);

  // Update countdown timer
  useEffect(() => {
    if (timeUntilNameChange !== null && timeUntilNameChange > 0) {
      const timer = setInterval(() => {
        setTimeUntilNameChange(prev => {
          if (prev === null || prev <= 1) {
            return null;
          }
          return prev - 1;
        });
      }, 60000); // Update every minute

      return () => clearInterval(timer);
    }
  }, [timeUntilNameChange]);


  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('Kích thước file không được vượt quá 5MB', 'error');
        return;
      }
      if (!file.type.startsWith('image/')) {
        showToast('File phải là hình ảnh', 'error');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChangeName = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await userService.changeName(nameChangeData.first_name, nameChangeData.last_name);
      window.dispatchEvent(new Event('auth-changed'));
      showToast('Đổi tên thành công', 'success');
      setShowChangeNameModal(false);
      // Update profile data
      setProfileData(prev => ({
        ...prev,
        first_name: nameChangeData.first_name,
        last_name: nameChangeData.last_name,
      }));
      // Reset timer
      setTimeUntilNameChange(24 * 60); // 24 hours in minutes
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể đổi tên', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let avatarUrl = user?.avatar;
      if (avatarFile && user) {
        const uploadedUser = await userService.uploadAvatar(user.id, avatarFile);
        avatarUrl = uploadedUser.avatar;
      }

      await userService.updateProfileSettings({
        avatar: avatarUrl,
        bio: profileData.bio,
        location: profileData.location,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
      });

      window.dispatchEvent(new Event('auth-changed'));
      showToast('Cập nhật hồ sơ thành công', 'success');
      setAvatarFile(null);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể cập nhật hồ sơ', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours} giờ ${mins} phút`;
    }
    return `${mins} phút`;
  };

  const toggleSection = (section: 'socialLinks' | 'membershipVisibility' | 'advanced') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAccountUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userService.updateAccountSettings({
        email: accountData.email,
        phone: accountData.phone,
      });
      window.dispatchEvent(new Event('auth-changed'));
      showToast('Cập nhật thông tin tài khoản thành công', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể cập nhật thông tin', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordData.current_password) {
      showToast('Vui lòng nhập mật khẩu hiện tại', 'error');
      return;
    }

    if (!passwordData.new_password) {
      showToast('Vui lòng nhập mật khẩu mới', 'error');
      return;
    }

    if (passwordData.new_password.length < 8) {
      showToast('Mật khẩu mới phải có ít nhất 8 ký tự', 'error');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      showToast('Mật khẩu mới và xác nhận mật khẩu không khớp', 'error');
      return;
    }

    setLoading(true);
    try {
      await userService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      // Show modal instead of toast
      setShowPasswordChangeModal(true);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể đổi mật khẩu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleKeepLoggedIn = () => {
    setShowPasswordChangeModal(false);
    showToast('Đổi mật khẩu thành công. Bạn vẫn đăng nhập trên thiết bị này.', 'success');
    window.dispatchEvent(new Event('auth-changed'));
  };

  const handleLogoutAllDevices = () => {
    setShowPasswordChangeModal(false);
    authService.logout();
    showToast('Đã đăng xuất khỏi thiết bị này', 'success');
    navigate('/login');
  };

  const handleSectionChange = (section: SettingsSection) => {
    setActiveSection(section);
    navigate(`/settings/${section}`);
  };

  const handlePrivacyUpdate = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      showToast('Cập nhật cài đặt quyền riêng tư thành công', 'success');
    } catch (err: any) {
      showToast('Không thể cập nhật cài đặt quyền riêng tư', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      showToast('Cập nhật cài đặt thông báo thành công', 'success');
    } catch (err: any) {
      showToast('Không thể cập nhật cài đặt thông báo', 'error');
    } finally {
      setLoading(false);
    }
  };


  if (authLoading) {
    return (
      <div className="settings-page">
        <div className="settings-loading">{t('settings.loading', { defaultValue: 'Loading...' })}</div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-container">
        {/* Sidebar */}
        <div className="settings-sidebar">
          <nav className="settings-nav">
            <button
              onClick={() => handleSectionChange('community')}
              className={`settings-nav-item ${activeSection === 'community' ? 'active' : ''}`}
            >
              {t('settings.menu.community', { defaultValue: 'Community' })}
            </button>
            <button
              onClick={() => handleSectionChange('profile')}
              className={`settings-nav-item ${activeSection === 'profile' ? 'active' : ''}`}
            >
              {t('settings.menu.profile', { defaultValue: 'Profile' })}
            </button>
            <button
              onClick={() => handleSectionChange('payment')}
              className={`settings-nav-item ${activeSection === 'payment' ? 'active' : ''}`}
            >
              {t('settings.menu.payment', { defaultValue: 'Payment' })}
            </button>
            <button
              onClick={() => handleSectionChange('account')}
              className={`settings-nav-item ${activeSection === 'account' ? 'active' : ''}`}
            >
              {t('settings.menu.account', { defaultValue: 'Account' })}
            </button>
            <button
              onClick={() => handleSectionChange('notifications')}
              className={`settings-nav-item ${activeSection === 'notifications' ? 'active' : ''}`}
            >
              {t('settings.menu.notifications', { defaultValue: 'Notifications' })}
            </button>
            <button
              onClick={() => handleSectionChange('chat')}
              className={`settings-nav-item ${activeSection === 'chat' ? 'active' : ''}`}
            >
              {t('settings.menu.chat', { defaultValue: 'Chat' })}
            </button>
            <button
              onClick={() => handleSectionChange('payment-methods')}
              className={`settings-nav-item ${activeSection === 'payment-methods' ? 'active' : ''}`}
            >
              {t('settings.menu.paymentMethods', { defaultValue: 'Payment Methods' })}
            </button>
            <button
              onClick={() => handleSectionChange('payment-history')}
              className={`settings-nav-item ${activeSection === 'payment-history' ? 'active' : ''}`}
            >
              {t('settings.menu.paymentHistory', { defaultValue: 'Payment History' })}
            </button>
            <button
              onClick={() => handleSectionChange('theme')}
              className={`settings-nav-item ${activeSection === 'theme' ? 'active' : ''}`}
            >
              {t('settings.menu.theme', { defaultValue: 'Theme' })}
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="settings-content">
          {/* Community Section */}
          {activeSection === 'community' && (
            <div className="settings-section">
              <CommunitySettings />
            </div>
          )}

          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="settings-section">
              <h1 className="settings-section-title">Hồ sơ</h1>

              <div className="settings-form">
                <form onSubmit={handleProfileUpdate}>
                  {/* Profile Photo */}
                  <div className="profile-photo-section">
                    <div className="profile-photo-container">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Profile" className="profile-photo" />
                      ) : (
                        <div className="profile-photo-placeholder">
                          {(user?.full_name || user?.username || 'U')[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    <label className="change-photo-link">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        style={{ display: 'none' }}
                      />
                      Đổi ảnh đại diện
                    </label>
                  </div>

                  {/* Name Fields */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Họ</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.first_name}
                        onChange={(e) => setProfileData((prev) => ({ ...prev, first_name: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Tên</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.last_name}
                        onChange={(e) => setProfileData((prev) => ({ ...prev, last_name: e.target.value }))}
                      />
                    </div>
                  </div>
                  <p className="form-hint">
                    Bạn chỉ có thể đổi tên một lần mỗi 24 giờ, và phải sử dụng tên thật của bạn.{' '}
                    {timeUntilNameChange !== null && timeUntilNameChange > 0 ? (
                      <span className="text-gray-600">
                        Có thể đổi tên sau {formatTimeRemaining(timeUntilNameChange)}.
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowChangeNameModal(true)}
                        className="form-link"
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                      >
                        Đổi tên.
                      </button>
                    )}
                  </p>

                  {/* URL Field */}
                  <div className="form-group">
                    <label className="form-label">URL</label>
                    <div className="url-input-wrapper">
                      <span className="url-prefix">taothaoaipro.com/@</span>
                      <input
                        type="text"
                        className="form-input url-input"
                        value={user?.username || ''}
                        readOnly
                      />
                    </div>
                    <p className="form-hint">
                      Bạn có thể đổi URL khi đã có 90 đóng góp, 30 người theo dõi và sử dụng trong 90 ngày.
                    </p>
                  </div>

                  {/* Bio Field */}
                  <div className="form-group">
                    <label className="form-label">Tiểu sử</label>
                    <textarea
                      className="form-textarea"
                      value={profileData.bio}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, bio: e.target.value }))}
                      maxLength={150}
                      rows={4}
                    />
                    <div className="char-counter">{profileData.bio.length}/150</div>
                  </div>

                  {/* Location Field */}
                  <div className="form-group">
                    <label className="form-label">Vị trí</label>
                    <input
                      type="text"
                      className="form-input"
                      value={profileData.location}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, location: e.target.value }))}
                      placeholder="Nhập vị trí của bạn"
                    />
                    <div className="location-actions">
                      <a href="#" className="form-link">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {' '}Đổi vị trí trên bản đồ
                      </a>
                      <a href="#" className="form-link text-gray-500">Xóa vị trí trên bản đồ</a>
                    </div>
                  </div>

                  {/* Collapsible Sections */}
                  <div className="collapsible-section">
                    <button
                      type="button"
                      className="collapsible-header"
                      onClick={() => toggleSection('socialLinks')}
                    >
                      <span>Liên kết xã hội</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 transition-transform ${expandedSections.socialLinks ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedSections.socialLinks && (
                      <div className="collapsible-content">
                        <p className="form-hint">Tính năng đang phát triển.</p>
                      </div>
                    )}
                  </div>

                  <div className="collapsible-section">
                    <button
                      type="button"
                      className="collapsible-header"
                      onClick={() => toggleSection('membershipVisibility')}
                    >
                      <span>Hiển thị thành viên</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 transition-transform ${expandedSections.membershipVisibility ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedSections.membershipVisibility && (
                      <div className="collapsible-content">
                        <p className="form-hint">Tính năng đang phát triển.</p>
                      </div>
                    )}
                  </div>

                  <div className="collapsible-section">
                    <button
                      type="button"
                      className="collapsible-header"
                      onClick={() => toggleSection('advanced')}
                    >
                      <span>Nâng cao</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 transition-transform ${expandedSections.advanced ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedSections.advanced && (
                      <div className="collapsible-content">
                        <p className="form-hint">Tính năng đang phát triển.</p>
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      disabled={loading}
                      className="settings-save-button"
                    >
                      {loading ? 'Đang lưu...' : 'Lưu thay đổi'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Account Section */}
          {activeSection === 'account' && (
            <div className="settings-section">
              <h1 className="settings-section-title">Tài khoản</h1>
              <p className="settings-section-description">
                Quản lý thông tin tài khoản của bạn.
              </p>

              {/* Account Information */}
              <div className="settings-form">
                <h2 className="settings-subsection-title">Thông tin tài khoản</h2>
                <form onSubmit={handleAccountUpdate} className="space-y-6">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={accountData.email}
                      onChange={(e) => setAccountData((prev) => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Số điện thoại</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={accountData.phone}
                      onChange={(e) => setAccountData((prev) => ({ ...prev, phone: e.target.value }))}
                      placeholder="Nhập số điện thoại"
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      type="submit"
                      disabled={loading}
                      className="settings-save-button"
                    >
                      {loading ? 'Đang lưu...' : 'Lưu thay đổi'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Change Password */}
              <div className="settings-form" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                <h2 className="settings-subsection-title">Đổi mật khẩu</h2>
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div className="form-group">
                    <label className="form-label">Mật khẩu hiện tại</label>
                    <input
                      type="password"
                      className="form-input"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData((prev) => ({ ...prev, current_password: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mật khẩu mới</label>
                    <input
                      type="password"
                      className="form-input"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData((prev) => ({ ...prev, new_password: e.target.value }))}
                      required
                      minLength={8}
                    />
                    <p className="form-hint">Mật khẩu phải có ít nhất 8 ký tự, bao gồm chữ hoa, chữ thường và số</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Xác nhận mật khẩu mới</label>
                    <input
                      type="password"
                      className="form-input"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData((prev) => ({ ...prev, confirm_password: e.target.value }))}
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      type="submit"
                      disabled={loading}
                      className="settings-save-button"
                    >
                      {loading ? 'Đang đổi mật khẩu...' : 'Đổi mật khẩu'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div className="settings-section">
              <h1 className="settings-section-title">Thông báo</h1>
              <p className="settings-section-description">
                Quản lý cài đặt thông báo của bạn.
              </p>
              <div className="settings-form">
                <div className="settings-option">
                  <div className="settings-option-content">
                    <div className="settings-option-title">Thông báo qua email</div>
                    <div className="settings-option-description">Nhận thông báo qua email</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationData.email_notifications}
                    onChange={(e) => setNotificationData((prev) => ({ ...prev, email_notifications: e.target.checked }))}
                    className="settings-checkbox"
                  />
                </div>
                <div className="settings-option">
                  <div className="settings-option-content">
                    <div className="settings-option-title">Thông báo đẩy</div>
                    <div className="settings-option-description">Nhận thông báo đẩy trên trình duyệt</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationData.push_notifications}
                    onChange={(e) => setNotificationData((prev) => ({ ...prev, push_notifications: e.target.checked }))}
                    className="settings-checkbox"
                  />
                </div>
                <div className="settings-option">
                  <div className="settings-option-content">
                    <div className="settings-option-title">Thông báo bình luận</div>
                    <div className="settings-option-description">Nhận thông báo khi có bình luận mới</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationData.comment_notifications}
                    onChange={(e) => setNotificationData((prev) => ({ ...prev, comment_notifications: e.target.checked }))}
                    className="settings-checkbox"
                  />
                </div>
                <div className="settings-option">
                  <div className="settings-option-content">
                    <div className="settings-option-title">Thông báo bài viết</div>
                    <div className="settings-option-description">Nhận thông báo về bài viết</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationData.post_notifications}
                    onChange={(e) => setNotificationData((prev) => ({ ...prev, post_notifications: e.target.checked }))}
                    className="settings-checkbox"
                  />
                </div>
                <div className="form-actions">
                  <button
                    onClick={handleNotificationUpdate}
                    disabled={loading}
                    className="settings-save-button"
                  >
                    {loading ? 'Đang lưu...' : 'Lưu thay đổi'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Other sections - Placeholder */}
          {(activeSection === 'payment' ||
            activeSection === 'chat' || activeSection === 'payment-methods' ||
            activeSection === 'payment-history' || activeSection === 'theme') && (
              <div className="settings-section">
                <h1 className="settings-section-title">
                  {activeSection === 'payment' && 'Thanh toán'}
                  {activeSection === 'chat' && 'Trò chuyện'}
                  {activeSection === 'payment-methods' && 'Phương thức thanh toán'}
                  {activeSection === 'payment-history' && 'Lịch sử thanh toán'}
                  {activeSection === 'theme' && 'Chủ đề'}
                </h1>
                <p className="settings-section-description">
                  Tính năng đang phát triển.
                </p>
              </div>
            )}
        </div>
      </div>

      {/* Change Name Modal */}
      {showChangeNameModal && (
        <div className="change-name-modal-overlay" onClick={() => setShowChangeNameModal(false)}>
          <div className="change-name-modal" onClick={(e) => e.stopPropagation()}>
            <div className="change-name-modal-header">
              <h2>Đổi tên</h2>
              <button className="change-name-modal-close" onClick={() => setShowChangeNameModal(false)}>×</button>
            </div>
            <form onSubmit={handleChangeName} className="change-name-modal-form">
              <p className="change-name-modal-warning">
                Bạn chỉ có thể đổi tên một lần mỗi 24 giờ. Vui lòng sử dụng tên thật của bạn.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Họ</label>
                  <input
                    type="text"
                    className="form-input"
                    value={nameChangeData.first_name}
                    onChange={(e) => setNameChangeData((prev) => ({ ...prev, first_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Tên</label>
                  <input
                    type="text"
                    className="form-input"
                    value={nameChangeData.last_name}
                    onChange={(e) => setNameChangeData((prev) => ({ ...prev, last_name: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="change-name-modal-actions">
                <button
                  type="button"
                  className="change-name-cancel-button"
                  onClick={() => setShowChangeNameModal(false)}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="change-name-submit-button"
                  disabled={loading || !nameChangeData.first_name.trim() || !nameChangeData.last_name.trim()}
                >
                  {loading ? 'Đang xử lý...' : 'Đổi tên'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Confirmation Modal */}
      {showPasswordChangeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Đổi mật khẩu thành công</h2>
            </div>
            <div className="modal-body">
              <p className="modal-message">
                Mật khẩu bạn vừa đổi, bạn có muốn duy trì đăng nhập trên thiết bị này không?
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-button modal-button-secondary"
                onClick={handleLogoutAllDevices}
              >
                Không, đăng xuất
              </button>
              <button
                type="button"
                className="modal-button modal-button-primary"
                onClick={handleKeepLoggedIn}
              >
                Có, duy trì đăng nhập
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
